import { cn } from "@/lib/utils"
import type { ProductResponseDto } from "@/api/api-client"

import type { InventoryLabels } from "./inventory-types"
import { isCriticalStockProduct, isLowStockProduct, shouldReorderProduct } from "./inventory-utils"

interface InventoryStockStatusProps {
  product: ProductResponseDto
  labels: Pick<InventoryLabels, "stock">
}

export function InventoryStockStatus({ product, labels }: InventoryStockStatusProps) {
  const isLowStock = isLowStockProduct(product)
  const isCritical = isCriticalStockProduct(product)

  return (
    <div className="space-y-4">
      <span className="text-xs font-black uppercase tracking-label text-muted-foreground">
        {labels.stock}
      </span>
      <div
        className={cn(
          "relative flex flex-col items-center justify-center overflow-hidden border-[1px] p-6 text-center transition-all duration-500",
          isCritical
            ? "border-destructive/30 bg-destructive/[0.03]"
            : isLowStock
              ? "border-amber-500/30 bg-amber-50/50"
              : "border-foreground/10 bg-muted/10"
        )}
      >
        <span
          className={cn(
            "font-display text-6xl leading-none tracking-tighter",
            isCritical ? "text-destructive" : isLowStock ? "text-amber-600" : "text-foreground"
          )}
        >
          {product.stockQuantity}
        </span>
        <span className="mt-2 text-xs font-black uppercase tracking-widest">
          {product.weightUnit}S
        </span>
        <div
          className={cn(
            "absolute bottom-0 left-0 right-0 h-[2px] transition-all duration-700",
            isCritical
              ? "bg-destructive shadow-[0_0_8px_rgba(239,68,68,0.5)]"
              : isLowStock
                ? "bg-amber-500"
                : "bg-accent"
          )}
        />
      </div>
      {shouldReorderProduct(product) && (
        <div className="animate-pulse bg-amber-500 px-3 py-1 text-center text-xs font-black uppercase tracking-label text-white">
          REORDER @ {Math.ceil(product.reorderPoint)}
        </div>
      )}
    </div>
  )
}
